import { useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Repeat, Plus, Play } from "lucide-react";

const FREQ = ["weekly", "biweekly", "monthly"];

export default function RecurringPage() {
  const [items, setItems] = useState([]);
  const [plots, setPlots] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    plot_id: "", title: "", description: "", priority: "medium",
    frequency: "weekly", assigned_to: "", start_date: new Date().toISOString().slice(0, 10),
  });

  const load = async () => {
    const [r, p, u] = await Promise.all([
      api.get("/recurring"),
      api.get("/plots"),
      api.get("/users?role=field_worker"),
    ]);
    setItems(r.data); setPlots(p.data); setWorkers(u.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/recurring", { ...form, assigned_to: form.assigned_to || null });
      toast.success("Schedule created");
      setOpen(false);
      setForm({ plot_id: "", title: "", description: "", priority: "medium", frequency: "weekly", assigned_to: "", start_date: new Date().toISOString().slice(0, 10) });
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const toggle = async (r, active) => {
    try {
      await api.patch(`/recurring/${r.id}`, { active });
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const runNow = async () => {
    try {
      const { data } = await api.post("/recurring/run-now");
      toast.success(`${data.created} task(s) generated`);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Repeat className="text-primary" />
          <h1 className="heading text-3xl md:text-4xl">Recurring maintenance</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runNow} data-testid="run-now-btn"><Play size={14} className="mr-2" />Run due now</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-recurring-btn"><Plus size={14} className="mr-2" />New schedule</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New recurring schedule</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label>Plot</Label>
                  <Select value={form.plot_id} onValueChange={(v) => setForm({ ...form, plot_id: v })}>
                    <SelectTrigger data-testid="rec-plot-select"><SelectValue placeholder="Select plot" /></SelectTrigger>
                    <SelectContent>{plots.map((p) => (<SelectItem key={p.id} value={p.id}>Plot {p.plot_number}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div><Label>Title</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Weekly drip irrigation" data-testid="rec-title-input" /></div>
                <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Frequency</Label>
                    <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                      <SelectTrigger data-testid="rec-freq-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{FREQ.map((f) => (<SelectItem key={f} value={f}>{f}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{["low", "medium", "high"].map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Start date</Label><Input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="rec-start-input" /></div>
                <div>
                  <Label>Assign to</Label>
                  <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                    <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>{workers.map((w) => (<SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <DialogFooter><Button type="submit" disabled={!form.plot_id || !form.title} data-testid="rec-save-btn">Create</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-6">Templates are materialised into real maintenance tasks daily at 6:00 IST. Runs today advance the next-run date by the schedule cadence.</p>

      <div className="border border-border rounded-lg bg-card divide-y divide-border">
        {items.length === 0 && <div className="p-6 text-sm text-muted-foreground">No recurring schedules yet.</div>}
        {items.map((r) => {
          const plot = plots.find((p) => p.id === r.plot_id);
          const worker = workers.find((w) => w.id === r.assigned_to);
          return (
            <div key={r.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`recurring-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{r.title}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                  Plot {plot?.plot_number || "—"} · {r.frequency} · Next {r.next_run_at} · {worker?.name || "Unassigned"} · Runs {r.runs || 0}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={r.active} onCheckedChange={(v) => toggle(r, v)} data-testid={`toggle-${r.id}`} />
                <span className="text-xs">{r.active ? "Active" : "Paused"}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
